import type { MouseEvent, PropsWithChildren } from 'react';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { getLink } from '@frameio/shared/routing';
import { LinkWithPrefetch } from '@frameio/shared/routing/components/LinkWithPrefetch';
import { consumeEvent } from '@frameio/utils';
import { Collapse, CollapseContent, Text, theme } from '@frameio/vapor';
import { useOpenWorkspaceProjectMenu } from '../../../../hooks';
import { useWorkspaceNavigationItemInfoFragment } from '../../gql/generated/WorkspaceNavigationItemInfo';
import { CollapseTrigger } from './CollapseTrigger';
import { ContextMenuButton } from './ContextMenuButton';

type WorkspaceRowProps = {
  workspaceId: string;
  contextMenuDisabled?: boolean;
  onMouseEnter?: (evt: React.MouseEvent) => void;
  onMouseLeave?: (evt: React.MouseEvent) => void;
  shouldExpand?: boolean;
};

export const WorkspaceRow = ({
  children,
  contextMenuDisabled = false,
  workspaceId,
  shouldExpand = true,
  ...hoverProps
}: PropsWithChildren<WorkspaceRowProps>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const openContextMenu = useOpenWorkspaceProjectMenu();

  const { data } = useWorkspaceNavigationItemInfoFragment(workspaceId);

  const handleContextMenu = (event?: MouseEvent) => {
    if (contextMenuDisabled) return;

    if (event) {
      consumeEvent(event);
    }

    openContextMenu(
      { id: workspaceId, type: 'workspace' },
      containerRef,
      event
    );
  };

  return (
    <Collapse
      isShowing={shouldExpand && isExpanded}
      onRequestChange={setIsExpanded}
    >
      <WorkspaceRowContainer
        ref={containerRef}
        {...hoverProps}
        onContextMenu={handleContextMenu}
      >
        <CollapseTrigger isExpanded={isExpanded} />
        <WorkspaceLink
          data-dd-action-name="workspace-nav.workspace-link"
          href={getLink({ page: 'workspaces', workspaceId })}
        >
          <WorkspaceName enableTooltip={true} truncate={true} variant="body">
            {data?.name}
          </WorkspaceName>
        </WorkspaceLink>
        {!contextMenuDisabled && (
          <ContextMenuButton onContextMenu={handleContextMenu} />
        )}
      </WorkspaceRowContainer>

      <CollapseContent>{children}</CollapseContent>
    </Collapse>
  );
};

export const WorkspaceRowContainer = styled.div`
  align-items: center;
  border-radius: ${theme.radii[6]};
  display: flex;
  gap: ${theme.spaces[8]};
  padding: ${theme.spaces[4]} ${theme.spaces[8]};
  width: 100%;

  &:hover {
    background: ${theme.colors.background.interactive.hover};
  }

  &:focus-visible {
    background: ${theme.colors.background.interactive.hover};
    box-shadow: inset 0 0 0 1px ${theme.colors.border.interactive.focused};
  }
`;

const WorkspaceLink = styled(LinkWithPrefetch)`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 1;
  justify-content: space-between;
  min-width: 0;
`;

export const WorkspaceName = styled(Text)`
  color: ${theme.colors.text.secondary};
  font-weight: ${theme.fontWeights.medium};
  line-height: ${theme.lineHeights[100]};
`;
